import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    Group,
    Heading,
    Image,
    ImagePreview,
    ImagePreviewIcon,
    ImagePreviewRemoveButton,
    LoadingIndicator,
    LoadingPanel,
    Switch,
} from '@gitbook/spine-react';
import { isUrl } from '@gitbook/util-url';

import { SiteSpacePicker } from '../../../../../Components';
import {
    SiteCustomizationContextProvider,
    useSiteCustomizationContext,
} from '../../../../../Components/SiteCustomizationFlow/SiteCustomizationContext';
import { useSiteContext } from '../../../../../state';
import { useUploadFile } from '../../../../../utils';
import { useSiteSettingsContext } from '../SiteSettingsContext';

/**
 * Settings to configure the preview image of a site when shared on social media.
 *
 * Can be defined at site-level and for each site space.
 */
export function SocialPreview() {
    return (
        <ActionableContainer orientation="horizontal">
            <Group>
                <Heading>Social preview</Heading>
                <Description>
                    Upload an image to customize your content's social media preview.
                </Description>
            </Group>
            <SocialPreviewButton />
        </ActionableContainer>
    );
}

/**
 * Settings to configure the tracking of a site. Can only be defined at site-level.
 */
export function GitBookTracking() {
    const { updateSiteSettings, siteSettings, savingSiteSettings } = useSiteSettingsContext();

    return (
        <ActionableContainer orientation="horizontal">
            <Group>
                <Heading>Insights cookie</Heading>
                <Description>
                    GitBook uses cookies to identify returning visitors and gather insights on your
                    site's performance. This includes information about user interactions, such as
                    page views and other engagement metrics.
                </Description>
            </Group>
            <Switch
                loading={savingSiteSettings}
                value={siteSettings.insights.trackingCookie}
                onValueChange={(value) => {
                    updateSiteSettings(
                        {
                            insights: {
                                trackingCookie: value,
                            },
                        },
                        { title: 'Insights cookie settings saved' }
                    );
                }}
            />
        </ActionableContainer>
    );
}

function SocialPreviewButton() {
    return (
        <DialogRoot>
            <DialogTrigger>
                <Button kind="secondary" label="Manage social preview" leadingIcon={Icon.Image} />
            </DialogTrigger>
            <SocialPreviewDialog />
        </DialogRoot>
    );
}

function SocialPreviewDialog() {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Social preview</Heading>
                <Description>
                    Enhance your social media presence by customizing the image displayed when your
                    content is shared.
                </Description>
            </DialogHeader>
            <DialogBody>
                <SocialPreviewForm />
            </DialogBody>
        </Dialog>
    );
}

function SocialPreviewForm() {
    const { site, organizationId } = useSiteContext();
    const [selectedSiteSpaceId, setSelectedSiteSpaceId] = React.useState<
        api.SiteSpace['id'] | undefined
    >(undefined);
    return (
        <React.Suspense fallback={<LoadingPanel />}>
            <SiteCustomizationContextProvider
                site={site}
                siteSpaceId={selectedSiteSpaceId}
                organizationId={organizationId}
            >
                <SocialPreviewUpload
                    selectedSiteSpaceId={selectedSiteSpaceId}
                    setSelectedSiteSpaceId={setSelectedSiteSpaceId}
                />
            </SiteCustomizationContextProvider>
        </React.Suspense>
    );
}

function SocialPreviewUpload(props: {
    selectedSiteSpaceId: api.SiteSpace['id'] | undefined;
    setSelectedSiteSpaceId: React.Dispatch<React.SetStateAction<api.SiteSpace['id'] | undefined>>;
}) {
    const { selectedSiteSpaceId, setSelectedSiteSpaceId } = props;
    const { site, organizationId } = useSiteContext();
    const hasMultipleSiteSpaces = site.siteSpaces > 1;
    const { savingSettings } = useSiteCustomizationContext();
    const [uploading, setUploading] = React.useState(false);

    return (
        <div className="flex flex-col gap-6">
            {hasMultipleSiteSpaces ? (
                <React.Suspense fallback={<LoadingIndicator className="mx-auto" />}>
                    <div className="flex">
                        <SiteSpacePicker
                            siteId={site.id}
                            organizationId={organizationId}
                            selectedId={selectedSiteSpaceId}
                            onSelect={(value) => setSelectedSiteSpaceId(value)}
                            disabled={uploading || savingSettings}
                        />
                    </div>
                </React.Suspense>
            ) : null}

            <React.Suspense fallback={<LoadingIndicator className="mx-auto" />}>
                <SocialImagePreviewUploader uploading={uploading} setUploading={setUploading} />
            </React.Suspense>
        </div>
    );
}

function SocialImagePreviewUploader(props: {
    uploading: boolean;
    setUploading: React.Dispatch<boolean>;
}) {
    const { site, organizationId, siteSpaceId, settings, updateSettings, savingSettings } =
        useSiteCustomizationContext();

    const promptAndUpload = useUploadFile();
    const onPressUpload = async () => {
        props.setUploading(true);
        const metadata = await promptAndUpload(
            {
                kind: 'site_socialpreview',
                organization: organizationId,
                site: site.id,
                accept: 'png-jpg-images',
            },
            {
                loading: 'Uploading image…',
                success: 'Image uploaded',
            }
        );

        if (metadata && isUrl(metadata.downloadURL)) {
            updateSettings(
                {
                    socialPreview: {
                        url: metadata.downloadURL,
                    },
                },
                {
                    title: 'Social preview saved',
                }
            );

            analytics.logEvent(
                analytics.createSiteEvent(
                    { id: site.id, organization: organizationId },
                    {
                        type: 'site_edit_publish_upload_socialpreview',
                    }
                )
            );
        }

        props.setUploading(false);
    };

    const removeSocialPreview = () => {
        updateSettings(
            {
                socialPreview: {
                    url: siteSpaceId ? null : undefined,
                },
            },
            { title: 'Social preview removed' }
        );
    };

    const loading = props.uploading || savingSettings;

    return (
        <div className="flex flex-col gap-2">
            <ImagePreview
                onPress={onPressUpload}
                className="w-full"
                style={{ aspectRatio: '1200 / 630' }}
                disabled={loading}
            >
                {loading ? (
                    <LoadingPanel />
                ) : settings.socialPreview?.url ? (
                    <>
                        <Image
                            src={settings.socialPreview.url}
                            className="max-h-full w-full object-contain"
                            rounded={false}
                        />
                        <ImagePreviewRemoveButton onPress={removeSocialPreview} />
                    </>
                ) : (
                    <div className="flex items-center gap-2">
                        <ImagePreviewIcon /> Upload image
                    </div>
                )}
            </ImagePreview>
            <p className="text-muted">For optimal results, use an image of 1280×630 pixels.</p>
        </div>
    );
}
